/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

/* eslint-disable @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any --
 * The report document and pcg-report core are loose-typed by design: the
 * core is vendored prototype JS with a hand-written surface .d.ts. */

import React, { ReactElement, useRef } from "react"
import {
  BIDIRECTIONAL_ZONES,
  UNIDIRECTIONAL_ZONES,
  UNRESOLVED_SUGGESTED_INTERPRETATION,
  canSaveFeatureToReport,
  currentHeartRateBpm,
  displayMarkerPosition,
  displayReferenceRange,
  calculateMarkerPosition,
  effectiveFeatureColorClass,
  featureVisibleForReport,
  heartRateRangeOf,
  inclusionStateOf,
  inclusionStateSummary,
  isAssessmentUnset,
  isCandidateAwaitingReview,
  isCardEditable,
  isCardEditing,
  isHeartRateFeature,
} from "./core"
import {
  adoptSuggestedClinicianNote,
  applyMarkerDragTick,
  beginFeatureEdit,
  cancelFeatureEdits,
  commitClinicianNote,
  completeMarkerMove,
  keepClinicianNote,
  rewindFeature,
  saveFeatureToReport,
  selectStructuralSource,
  setFeatureReportInclusion,
  suggestedClinicianNote,
  toggleTimingSource,
  undoFeatureReportInclusion,
} from "./edit/edit-actions"
import HeartReportEvidencePanel from "./HeartReportEvidencePanel"
import { logReportAction } from "./report-action-log"
import HeartReportStructuralSources from "./HeartReportStructuralSources"
import HeartReportTimingSources from "./HeartReportTimingSources"
// The track variant and zone labels come from the module the report view's
// cards use, so the scale reads the same in both.
import { scaleLabels, scaleRangePct, scaleTrackVariant } from "./scale"

/**
 * Clinical-review card grid — React rebuild of the prototype's admin card
 * body (cardiac_report_v0_5_8.html). Every standard card shows here,
 * whatever stands behind it:
 *
 * - in the report and open for editing (every accepted card is, from the
 *   moment the report is unlocked): Save / Cancel / Exclude — Save closes
 *   the card's edit session, recording a change when there is one
 * - in the report after a per-card Save: read-only, with Edit / Exclude
 * - nothing accepted behind it yet: Save / Cancel / Exclude, starting from a
 *   neutral dotted marker and an Observed Value of "To be manually set";
 *   Save stays disabled until the clinician types a value or moves the marker
 * - pending (accepted Timing recordings that disagree, or candidate evidence
 *   from a recording the review did not accept for the card's domain): the
 *   derived value with a dotted marker and "Derived value — review and Save,
 *   edit, or Exclude", until the clinician acts on the card
 * - excluded: Restore for review, under an "Excluded from report" bar
 * - a SYS:DIA Ratio or SYS:DIA Duration card with no measurement behind it:
 *   "Not included — measurement unavailable", locked, with nothing to
 *   restore until evidence supplies the measurement
 *
 * A card in the report carries no state bar; a card that still needs the
 * clinician says so in plain text above its value.
 *
 * The General Explanation and Patient Result Wording are product-controlled
 * and shown read-only, as is the Suggested clinical interpretation. The one
 * thing the clinician writes is the optional Clinician Note, which starts
 * empty; "Use suggested wording" copies the suggestion in as the clinician's
 * own. A note written against an earlier result raises a banner the clinician
 * resolves with "Keep my note" or "Use updated suggested note".
 *
 * The four timing cards list the recordings the review accepted for Timing
 * ("Choose recordings to include", HeartReportTimingSources) and derive
 * their value from the ticked ones: a range where the recordings differ,
 * shown as two markers joined by a band, with the marker at a
 * representative median. Only Heart Rate's ends can be dragged, and its
 * Observed Value opens the editor (upstream also opens it from the track;
 * the value is the keyboard-reachable control here). Either end of a
 * SYS:DIA range opens the editor too (upstream also opens it from the
 * track). When the accepted
 * Timing recordings disagreed on the heart rate the four cards arrive
 * pending: the clinician chooses the recordings, adjusts the value if
 * needed, then saves or excludes each card.
 *
 * The Structural Sound card lists every recording the review accepted for
 * Structural Sound ("Accepted Structural Sound recordings",
 * HeartReportStructuralSources): the one its value came from is selected,
 * another assessable one can be chosen instead, and a recording with
 * nothing scored stays listed with the reason it cannot be chosen.
 *
 * Deliberate deviations from the prototype:
 * - Pointer events (with capture) instead of document-level mouse listeners —
 *   adds touch support; same clamp (5–95) and same drop cascade.
 * - The Clinician Note commits on blur exactly like the prototype, but the
 *   full re-render happens through React keyed by `revision` instead of an
 *   innerHTML rebuild.
 * - Not ported: create-component card, custom-feature delete, marker
 *   double-click admin backdoor.
 *
 * Under the scale and the recording checklist each card carries its
 * Evidence panel (the prototype's feed inspector; see
 * HeartReportEvidencePanel): information for the person preparing the
 * report, never shown in the report views or documents.
 *
 * The `features` array is MUTATED in place by the edit actions (the core rule
 * layer's contract); `onChanged` tells the owner to bump `revision` and mark
 * the document dirty for the next save. Note text enters the document on
 * blur only, but `onDraftInput` fires on every keystroke so the owner can
 * arm its Save control right away — without re-rendering, which would reset
 * the textarea being typed in. Starting an edit session changes nothing in
 * the document, so it re-renders through `onSessionChanged` without marking
 * it dirty.
 */

const featureHasDraftEdits = (feature: { reviewTouched?: boolean; noteToDoctorEdited?: boolean }): boolean =>
  Boolean(feature.reviewTouched || feature.noteToDoctorEdited)

// The ends of a SYS:DIA range: shown, never dragged
const FIXED_ENDPOINT_CLASSES = "scale-marker phr-edit-marker phr-edit-marker--endpoint phr-edit-marker--fixed"

// Where the ghost marker of a card with no assessment sits: mid-normal.
const neutralMarkerPosition = (feature: any): number =>
  feature.scaleType === "unidirectional"
    ? UNIDIRECTIONAL_ZONES.greenEnd / 2
    : (BIDIRECTIONAL_ZONES.amberLowEnd + BIDIRECTIONAL_ZONES.greenEnd) / 2

const HeartReportEditCards = ({
  features,
  questionnaire = null,
  revision,
  onChanged,
  onSessionChanged,
  onDraftInput,
  onRequestValueEdit,
  onNotice,
}: {
  features: any[]
  /** The report's questionnaire, for the Evidence panels' context line. */
  questionnaire?: unknown
  revision: number
  onChanged: () => void
  /** A card entered its edit session: re-render, nothing to save. */
  onSessionChanged: () => void
  /** A keystroke in the note editor: unsaved text exists, nothing re-rendered. */
  onDraftInput?: () => void
  onRequestValueEdit: (index: number) => void
  /** A refused action's reason. */
  onNotice?: (message: string) => void
}): ReactElement => {
  const hr = currentHeartRateBpm(features)
  const dragRef = useRef<{
    index: number
    endpoint: "min" | "max" | null
    startedAt: number
    start: Record<string, unknown>
  } | null>(null)
  // A card's marker state as the interaction diagnostics record it
  const markerState = (feature: any): Record<string, unknown> => ({
    observed: feature?.observed,
    status: feature?.status,
    markerPosition: feature?.markerPosition,
    range: heartRateRangeOf(feature),
  })

  const handleTrackPointerDown = (event: React.PointerEvent<HTMLDivElement>, index: number) => {
    // Prototype: only the marker dot starts a drag (track has no click-to-set);
    // the ends of a SYS:DIA range show the included recordings and stay put
    const target = event.target as HTMLElement
    if (
      !isCardEditable(features[index]) ||
      !target.classList.contains("scale-marker") ||
      target.classList.contains("phr-edit-marker--fixed")
    ) {
      return
    }
    const endpoint =
      target.dataset.endpoint === "min" || target.dataset.endpoint === "max" ? target.dataset.endpoint : null
    const start = markerState(features[index])
    dragRef.current = { index, endpoint, startedAt: performance.now(), start }
    logReportAction("marker_drag_started", {
      featureIndex: index,
      featureName: features[index]?.name || "Unknown",
      mode: endpoint ? "range" : "single",
      endpoint: endpoint || undefined,
      start,
    })
    // Optional-chained: jsdom (tests) has no pointer-capture implementation
    event.currentTarget.setPointerCapture?.(event.pointerId)
    event.preventDefault()
  }

  const handleTrackPointerMove = (event: React.PointerEvent<HTMLDivElement>, index: number) => {
    if (dragRef.current?.index !== index) {
      return
    }
    const rect = event.currentTarget.getBoundingClientRect()
    const percentage = ((event.clientX - rect.left) / rect.width) * 100
    applyMarkerDragTick(features, index, percentage, dragRef.current.endpoint)
    onChanged()
  }

  const handleTrackPointerUp = (event: React.PointerEvent<HTMLDivElement>, index: number) => {
    if (dragRef.current?.index !== index) {
      return
    }
    const { endpoint, startedAt, start } = dragRef.current
    dragRef.current = null
    event.currentTarget.releasePointerCapture?.(event.pointerId)
    const detail = {
      featureIndex: index,
      featureName: features[index]?.name || "Unknown",
      durationMs: Math.round(performance.now() - startedAt),
    }
    try {
      completeMarkerMove(features, index)
    } catch (error) {
      logReportAction("marker_drag_failed", { ...detail, message: String((error as Error)?.message || error) })
      throw error
    }
    logReportAction("marker_drag_completed", {
      ...detail,
      mode: endpoint ? "range" : "single",
      endpoint: endpoint || undefined,
      start,
      end: markerState(features[index]),
    })
    onChanged()
  }

  const changed = (result: boolean): void => {
    if (result) {
      onChanged()
    }
  }

  const saveCard = (index: number): void => {
    const result = saveFeatureToReport(features, index)
    if (result.ok) {
      onChanged()
    } else if (result.reason) {
      onNotice?.(result.reason)
    }
  }

  // A tick on a timing card's recording list: the card re-derives its
  // value, or the refusal is shown under the list
  const toggleSource = (index: number, recordingId: string, included: boolean): string | null => {
    const result = toggleTimingSource(features, index, recordingId, included)
    if (result.ok) {
      onChanged()
    }
    return result.reason
  }

  // A choice on the Structural Sound card's recording list: the card takes
  // that recording's result, or the refusal is shown under the list
  const chooseStructuralSource = (index: number, recordingId: string): string | null => {
    const result = selectStructuralSource(features, index, recordingId)
    if (result.ok) {
      onChanged()
    }
    return result.reason
  }

  return (
    <div className="category-grid" data-testid="edit-cards">
      {features.map((feature: any, index: number) => {
        if (!featureVisibleForReport(feature, { adminMode: true })) {
          return null
        }
        const inclusionState = inclusionStateOf(feature)
        const inclusion = inclusionStateSummary(feature)
        const editable = isCardEditable(feature)
        const unset = isAssessmentUnset(feature)
        // No measurement behind a card that states one: nothing to show or set
        const unavailable = Boolean(feature.measurementUnavailable)
        // Derived evidence the clinician has not acted on yet
        const awaitingReview = isCandidateAwaitingReview(feature)
        const dotted = unset || unavailable || awaitingReview
        const colorClass = effectiveFeatureColorClass(feature, hr)
        const markerPos = unset
          ? neutralMarkerPosition(feature)
          : displayMarkerPosition(feature.markerPosition ?? calculateMarkerPosition(feature, hr))
        const range = unset || unavailable ? null : scaleRangePct(feature, hr)
        const heartRate = isHeartRateFeature(feature)
        const name = String(feature.name)
        const referenceRange = displayReferenceRange(feature, hr)
        // Heart Rate's ends can be dragged; a SYS:DIA range's ends show the
        // included recordings and stay put, and open the value editor
        const endpointTitle = (endpoint: "min" | "max", label: string): string => {
          if (awaitingReview) return `${label} · derived evidence; review required`
          if (!editable) return "Select Edit to change this assessment"
          return `${label} · drag to adjust the ${endpoint === "min" ? "minimum" : "maximum"}`
        }
        const fixedEndpointTitle = (label: string): string => {
          if (awaitingReview) return `${label} · derived evidence; review required`
          return editable ? `${label} · click to edit` : label
        }
        // A SYS:DIA range card's endpoints open the editor (real buttons; the
        // track stays a plain surface, as the Heart Rate track does)
        const rangeOpensEditor = !heartRate && !!range && editable
        const endpointMarker = (endpoint: "min" | "max", pct: number, label: string) =>
          heartRate ? (
            <div
              className={`scale-marker phr-edit-marker phr-edit-marker--endpoint${
                editable ? "" : " phr-edit-marker--locked"
              }${awaitingReview ? " phr-edit-marker--dotted" : ""}`}
              role="slider"
              aria-label={`${name} ${endpoint === "min" ? "minimum" : "maximum"} marker`}
              aria-valuenow={Math.round(pct)}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-disabled={!editable}
              tabIndex={0}
              title={endpointTitle(endpoint, label)}
              style={{ left: `${pct}%` }}
              data-endpoint={endpoint}
              data-testid={`edit-marker-${endpoint}-${name}`}
            />
          ) : rangeOpensEditor ? (
            <button
              type="button"
              className={`${FIXED_ENDPOINT_CLASSES} phr-edit-marker--clickable${
                awaitingReview ? " phr-edit-marker--dotted" : ""
              }`}
              aria-label={`Edit ${name} assessment`}
              title={fixedEndpointTitle(label)}
              style={{ left: `${pct}%` }}
              data-testid={`edit-marker-${endpoint}-${name}`}
              onClick={() => onRequestValueEdit(index)}
            />
          ) : (
            <div
              className={`${FIXED_ENDPOINT_CLASSES}${awaitingReview ? " phr-edit-marker--dotted" : ""}`}
              aria-hidden="true"
              title={fixedEndpointTitle(label)}
              style={{ left: `${pct}%` }}
              data-testid={`edit-marker-${endpoint}-${name}`}
            />
          )
        let valueTitle = "Select Edit to change this value"
        if (unavailable) valueTitle = "No calculated measurement is available"
        else if (editable) valueTitle = "Click to edit value"
        let observedText = feature.observed || "—"
        if (unset) observedText = "To be manually set"
        else if (unavailable) observedText = "Not available"
        let markerTitle = "Drag to set assessment"
        if (unavailable) markerTitle = "Measurement unavailable — excluded from report"
        else if (awaitingReview) markerTitle = "Derived evidence — review, accept, or edit this assessment."
        else if (!editable) markerTitle = "Select Edit to change this assessment"
        else if (unset) markerTitle = "To be manually set. Drag to set assessment."
        let suggestedText = ""
        if (unavailable) suggestedText = String(feature.measurementUnavailableReason || "")
        else if (unset) suggestedText = UNRESOLVED_SUGGESTED_INTERPRETATION
        else suggestedText = suggestedClinicianNote(features, index)
        // An excluded card can be restored, unless it is out for want of a measurement
        const excludedAction = unavailable ? (
          <span className="phr-card-action-note">Excluded until measurement evidence is available</span>
        ) : (
          <button
            type="button"
            className="phr-card-action"
            onClick={() => changed(undoFeatureReportInclusion(features, index))}
          >
            Restore for review
          </button>
        )
        return (
          <article
            key={name}
            className={`biomarker-card phr-edit-card${editable ? "" : " phr-edit-card--saved"}`}
            data-testid={`edit-card-${name}`}
            data-status={unset || unavailable ? "unset" : colorClass}
            data-inclusion={inclusionState}
          >
            <div className="card-header">
              <div className="card-title">{name}</div>
            </div>
            {inclusion ? (
              <output
                className={`phr-inclusion-bar phr-inclusion-bar--${inclusion.tone}`}
                data-testid={`inclusion-${name}`}
              >
                <span className="phr-inclusion-title">{inclusion.title}</span>
                <span className="phr-inclusion-detail">{inclusion.detail}</span>
                {inclusion.warning ? <span className="phr-inclusion-detail">{inclusion.warning}</span> : null}
              </output>
            ) : null}
            {feature.reviewRequired && feature.reviewReason && inclusionState === "accepted" ? (
              <div className="phr-review-flag" role="note" data-testid={`review-flag-${name}`}>
                Note — {feature.reviewReason}
              </div>
            ) : null}

            {feature.normalRange ? (
              <>
                <span className="phr-edit-label">Observed Value</span>
                <div
                  className={`card-value phr-edit-value${unset || unavailable ? " phr-edit-value--unset" : ""}${
                    editable ? "" : " phr-edit-value--locked"
                  }`}
                  data-testid={`edit-value-${name}`}
                  title={valueTitle}
                  onClick={editable ? () => onRequestValueEdit(index) : undefined}
                >
                  {observedText}
                </div>
              </>
            ) : null}

            <div className="scale-container">
              <div
                className={`scale-track ${scaleTrackVariant(feature)}`}
                onPointerDown={(event) => handleTrackPointerDown(event, index)}
                onPointerMove={(event) => handleTrackPointerMove(event, index)}
                onPointerUp={(event) => handleTrackPointerUp(event, index)}
                data-testid={`edit-track-${name}`}
              >
                {range ? (
                  <>
                    <div
                      className={`phr-scale-range${awaitingReview ? " phr-scale-range--dotted" : ""}`}
                      style={{ left: `${range.minPct}%`, width: `${Math.max(2, range.maxPct - range.minPct)}%` }}
                      aria-hidden="true"
                      data-testid={`range-band-${name}`}
                    />
                    {endpointMarker("min", range.minPct, range.minLabel)}
                    {endpointMarker("max", range.maxPct, range.maxLabel)}
                  </>
                ) : (
                  <div
                    className={`scale-marker phr-edit-marker${dotted ? " phr-edit-marker--dotted" : ""}${
                      editable ? "" : " phr-edit-marker--locked"
                    }`}
                    role="slider"
                    aria-label={`${name} assessment marker`}
                    aria-valuenow={Math.round(markerPos)}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-disabled={!editable}
                    tabIndex={0}
                    title={markerTitle}
                    style={{ left: `${markerPos}%` }}
                    data-testid={`edit-marker-${name}`}
                  />
                )}
              </div>
              <div className="scale-labels">
                {scaleLabels(feature).map((label, i) => (
                  <div key={`${label.text}-${i}`} className="scale-label" style={{ flex: `0 0 ${label.basisPct}%` }}>
                    {label.text}
                  </div>
                ))}
              </div>
            </div>
            {referenceRange ? (
              <div className="card-range" data-testid={`edit-range-${name}`}>
                {referenceRange}
              </div>
            ) : null}
            {heartRate && feature.mixedRange && range ? (
              <div className="phr-edit-mixed-hint" data-testid={`mixed-range-${name}`}>
                Mixed range · accepted recordings cross reference categories
              </div>
            ) : null}
            {!heartRate && range ? (
              <div className="phr-edit-range-hint" data-testid={`range-hint-${name}`}>
                Endpoint markers show the included range; Observed Value provides the exact values.
              </div>
            ) : null}
            {unset ? <div className="phr-edit-unset-hint">To be manually set</div> : null}
            {awaitingReview ? (
              <div className="phr-edit-unset-hint" data-testid={`derived-hint-${name}`}>
                Derived value — review and Save, edit, or Exclude
              </div>
            ) : null}
            {unavailable ? <div className="phr-edit-unset-hint">No calculated marker available</div> : null}

            <HeartReportTimingSources
              feature={feature}
              editable={editable}
              onToggle={(recordingId, included) => toggleSource(index, recordingId, included)}
            />
            <HeartReportStructuralSources
              feature={feature}
              editable={editable}
              onSelect={(recordingId) => chooseStructuralSource(index, recordingId)}
            />
            <HeartReportEvidencePanel feature={feature} questionnaire={questionnaire} />

            <div className="phr-edit-notes">
              <div className="phr-edit-note-head">
                <span>What this means</span>
              </div>
              <span className="phr-edit-label phr-edit-label--sub">General explanation</span>
              <div className="phr-edit-readonly" data-testid={`explanation-${name}`}>
                {feature.patientExplanationText || ""}
              </div>
              <span className="phr-edit-label phr-edit-label--sub">Patient result wording</span>
              <div className="phr-edit-readonly" data-testid={`result-wording-${name}`}>
                {feature.patientResultText || ""}
              </div>
              <span className="phr-edit-label phr-edit-label--sub phr-edit-label--sub-spaced">
                Suggested clinical interpretation
              </span>
              <div className="phr-edit-readonly" data-testid={`suggested-${name}`}>
                {suggestedText}
              </div>

              <div className="phr-edit-note-head">
                <span>Clinician Note</span>
                {editable && !unset ? (
                  <button
                    type="button"
                    className="phr-edit-adopt"
                    title="Copy the suggested interpretation into the Clinician Note"
                    aria-label={`Use suggested wording for the ${name} clinician note`}
                    onClick={() => {
                      adoptSuggestedClinicianNote(features, index)
                      onChanged()
                    }}
                  >
                    Use suggested wording
                  </button>
                ) : null}
              </div>
              {feature.noteToDoctorStale ? (
                <div className="phr-edit-stale" role="alert">
                  <span>⚠️ The assessment changed after this Clinician Note was edited.</span>
                  <span className="phr-edit-stale-actions">
                    <button
                      type="button"
                      onClick={() => {
                        keepClinicianNote(features, index)
                        onChanged()
                      }}
                    >
                      Keep my note
                    </button>
                    <button
                      type="button"
                      className="phr-edit-stale-primary"
                      onClick={() => {
                        adoptSuggestedClinicianNote(features, index)
                        onChanged()
                      }}
                    >
                      Use updated suggested note
                    </button>
                  </span>
                </div>
              ) : null}
              <textarea
                key={`doctor:${revision}`}
                className={`phr-edit-textarea${editable ? "" : " phr-edit-textarea--locked"}`}
                placeholder="Optional — add the reviewing clinician’s note..."
                aria-label={`${name} clinician note`}
                readOnly={!editable}
                aria-readonly={!editable}
                defaultValue={feature.noteToDoctorEdited ? feature.noteToDoctor || "" : ""}
                onBlur={
                  editable ? (event) => changed(commitClinicianNote(features, index, event.target.value)) : undefined
                }
                onChange={editable ? onDraftInput : undefined}
              />
            </div>

            <div className="phr-card-actions" data-testid={`card-actions-${name}`}>
              {editable && featureHasDraftEdits(feature) && inclusionState !== "excluded" ? (
                <button
                  type="button"
                  className="phr-edit-rewind"
                  title="Reset this card to the exported result"
                  aria-label={`Reset ${name} to exported result`}
                  onClick={() => changed(rewindFeature(features, index))}
                >
                  ↺
                </button>
              ) : null}
              {inclusionState === "excluded" ? (
                excludedAction
              ) : inclusionState === "accepted" && !isCardEditing(feature) ? (
                <>
                  <button
                    type="button"
                    className="phr-card-action phr-card-action--primary"
                    aria-label={`Edit ${name}`}
                    onClick={() => {
                      if (beginFeatureEdit(features, index)) {
                        onSessionChanged()
                      }
                    }}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="phr-card-action phr-card-action--danger"
                    aria-label={`Exclude ${name}`}
                    onClick={() => changed(setFeatureReportInclusion(features, index, "excluded"))}
                  >
                    Exclude
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="phr-card-action phr-card-action--primary"
                    aria-label={`Save ${name}`}
                    disabled={!canSaveFeatureToReport(feature)}
                    onClick={() => saveCard(index)}
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    className="phr-card-action"
                    aria-label={`Cancel ${name} changes`}
                    onClick={() => changed(cancelFeatureEdits(features, index))}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="phr-card-action phr-card-action--danger"
                    aria-label={`Exclude ${name}`}
                    onClick={() => changed(setFeatureReportInclusion(features, index, "excluded"))}
                  >
                    Exclude
                  </button>
                </>
              )}
            </div>
          </article>
        )
      })}
    </div>
  )
}

export default HeartReportEditCards
export { featureHasDraftEdits }
